import { useCallback } from "react";
import { Platform, Dimensions } from "react-native";
import { FilterState } from "../SparcWalkTypes";
import { SparcWalkRowData } from "../../../types/sparcWalk";
import { toSparcWalkRowData } from "../utils/sparcWalkDataTransformers";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { isIOSMobile } from "../utils/deviceDetection";

interface UseSparcWalkFiltersProps {
  filters: FilterState;
  fullSparcWalkData: SparcWalkRowData[];
  setSparcWalkData: (data: SparcWalkRowData[]) => void;
  setAppliedFilters: (filters: FilterState) => void;
  setApiError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  selectedLocation: any;
  walkType: string;
  hiddenWalk: string;
  page: number;
}

/**
 * Custom hook to handle SPARC Walk filtering logic
 * Manages both local (iOS mobile) and API-based filtering
 */
const useSparcWalkFilters = ({
  filters,
  fullSparcWalkData,
  setSparcWalkData,
  setAppliedFilters,
  setApiError,
  setIsLoading,
  selectedLocation,
  walkType,
  hiddenWalk,
  page,
}: UseSparcWalkFiltersProps) => {
  // Apply filters locally for iOS mobile to provide instant view update
  const applyFiltersForIOSMobile = useCallback(
    (filtersArg?: FilterState) => {
      const effectiveFilters = filtersArg ?? filters;

      setAppliedFilters({ ...effectiveFilters });
      setApiError(null);

      const filteredResults = fullSparcWalkData.filter((row) => {
        // Zone filter
        if (
          effectiveFilters.zone &&
          effectiveFilters.zone.displayText &&
          effectiveFilters.zone.displayText !== "All" &&
          row.zone !== effectiveFilters.zone.displayText
        ) {
          return false;
        }

        // Category filter
        if (
          effectiveFilters.category &&
          effectiveFilters.category.displayText &&
          effectiveFilters.category.displayText !== "All"
        ) {
          const rowCategory =
            row.category || row.details?.split(" - ")[0] || "";
          if (rowCategory !== effectiveFilters.category.displayText) {
            return false;
          }
        }

        // Created By filter
        if (
          effectiveFilters.Created_By &&
          row.Created_By !== effectiveFilters.Created_By
        ) {
          return false;
        }

        // Modified By filter
        if (
          effectiveFilters.Modified_By &&
          row.Modified_By !== effectiveFilters.Modified_By
        ) {
          return false;
        }

        // Action/Status filter
        if (effectiveFilters.action && effectiveFilters.action !== "") {
          const rowStatus = (row.status || "").toLowerCase();
          if (rowStatus !== effectiveFilters.action.toLowerCase()) {
            return false;
          }
        }

        // Period filter
        if (effectiveFilters.period && effectiveFilters.period !== "") {
          if (String(row.period ?? "") !== effectiveFilters.period) {
            return false;
          }
        }

        // Week filter
        if (effectiveFilters.week && effectiveFilters.week !== "") {
          if (String(row.week ?? "") !== effectiveFilters.week) {
            return false;
          }
        }

        // Year filter
        if (effectiveFilters.year && effectiveFilters.year !== "") {
          if (String(row.year ?? "") !== effectiveFilters.year) {
            return false;
          }
        }

        return true;
      });

      console.log(
        `🔍 iOS Mobile filtering: ${fullSparcWalkData.length} → ${filteredResults.length} results`
      );
      setSparcWalkData(filteredResults);
      return filteredResults;
    },
    [filters, fullSparcWalkData, setAppliedFilters, setApiError, setSparcWalkData]
  );

  // Apply filters via API call (for web/iPad and server sync)
  const applyFilters = useCallback(
    async (filtersArg?: FilterState) => {
      const effectiveFilters = filtersArg ?? filters;

      try {
        setIsLoading(true);
        console.log("Applying filters via API:", effectiveFilters);

        setAppliedFilters({ ...effectiveFilters });

        if (!selectedLocation) {
          setApiError("No location selected");
          return;
        }

        const location = encodeURIComponent(
          selectedLocation.displayText || "Hayward CA"
        );
        let endpoint = `/sparc-walks?location=${location}&walkType=${encodeURIComponent(
          walkType
        )}&hiddenWalk=${encodeURIComponent(
          hiddenWalk
        )}&page=${page}&pageSize=100`;

        // Handle zone filter
        if (
          effectiveFilters.zone &&
          typeof effectiveFilters.zone === "object" &&
          effectiveFilters.zone.displayText &&
          effectiveFilters.zone.displayText !== "All"
        ) {
          endpoint += `&zone=${encodeURIComponent(
            effectiveFilters.zone.displayText
          )}`;
        }

        // Handle category filter
        if (
          effectiveFilters.category &&
          typeof effectiveFilters.category === "object" &&
          effectiveFilters.category.displayText &&
          effectiveFilters.category.displayText !== "All"
        ) {
          endpoint += `&sparcCategory=${encodeURIComponent(
            effectiveFilters.category.displayText
          )}`;
        }

        // Handle string filters
        if (effectiveFilters.Created_By && effectiveFilters.Created_By !== "") {
          endpoint += `&createdBy=${encodeURIComponent(
            effectiveFilters.Created_By
          )}`;
        }
        if (
          effectiveFilters.Modified_By &&
          effectiveFilters.Modified_By !== ""
        ) {
          endpoint += `&finalizedBy=${encodeURIComponent(
            effectiveFilters.Modified_By
          )}`;
        }
        if (effectiveFilters.action && effectiveFilters.action !== "") {
          endpoint += `&status=${encodeURIComponent(effectiveFilters.action)}`;
        }
        if (effectiveFilters.period && effectiveFilters.period !== "") {
          endpoint += `&period=${encodeURIComponent(effectiveFilters.period)}`;
        }
        if (effectiveFilters.week && effectiveFilters.week !== "") {
          endpoint += `&week=${encodeURIComponent(effectiveFilters.week)}`;
        }
        if (effectiveFilters.year && effectiveFilters.year !== "") {
          endpoint += `&year=${encodeURIComponent(effectiveFilters.year)}`;
        }

        console.log("API Filter endpoint:", endpoint);

        const api = createApiClient();
        const json = await apiClient(api, endpoint, "GET");

        if (!json.data || !Array.isArray(json.data)) {
          throw new Error("Invalid data structure received");
        }

        const formattedData = json.data.map(toSparcWalkRowData);
        console.log(`✅ API Filter applied: ${formattedData.length} results`);

        setSparcWalkData(formattedData);
        setAppliedFilters({ ...effectiveFilters });
      } catch (err) {
        console.error("Error applying filters:", err);
        setApiError("Failed to apply filters");
      } finally {
        setIsLoading(false);
      }
    },
    [
      filters,
      selectedLocation,
      walkType,
      hiddenWalk,
      page,
      setIsLoading,
      setAppliedFilters,
      setApiError,
      setSparcWalkData,
    ]
  );

  return {
    applyFiltersForIOSMobile,
    applyFilters,
  };
};

export default useSparcWalkFilters;
